import { useNavigate } from "@tanstack/react-router";
import type { SmartCollabGroupWithCounts } from "@unbox/api";
import { ChevronDown, ChevronUp, InfoCircle, IntersectCircle, Pin02 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useTargetCollaborationsMetrics } from "@/api/collaborations";
import { usePinSmartCollabGroupCollaboration, useSmartCollabGroupCollaborations } from "@/api/smart-collab-groups";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SEEN_ROW_CLASS, useSeenRows } from "@/components/application/table/use-seen-rows";
import { AutomationsActiveSignal } from "@/components/automations/automations-active-signal";
import { RulesMatchIndicator } from "@/components/rules/rules-match-indicator";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { ExpiryStatusCell } from "@/pages/collaborations/expiry-status-cell";
import { GmvCell } from "@/pages/collaborations/gmv-cell";
import { ModifiedCell } from "@/pages/collaborations/modified-cell";
import { OverdueCell } from "@/pages/collaborations/overdue-cell";
import { ProductChips } from "@/pages/collaborations/product-chips";
import { SMART_GROUP_EDIT_DISABLED_REASON } from "@/pages/collaborations/smart-group-edit";
import {
  GroupExpansionSentinel,
  MetricSkeleton,
  resolveMetric,
  seenGroupId,
  TARGET_COLLABORATIONS_TABLE_KEY,
} from "@/pages/collaborations/target-collaborations-table";
import { ViewAutomationTriggersModal } from "@/pages/collaborations/view-automation-triggers-modal";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { Tooltip, TooltipTrigger } from "../base/tooltip/tooltip";

const numberFormatter = new Intl.NumberFormat("en-US");

export function SmartGroupCard({ group }: { group: SmartCollabGroupWithCounts }) {
  const navigate = useNavigate();
  const merchant = useMerchant();
  const { isSeen, markSeen } = useSeenRows(TARGET_COLLABORATIONS_TABLE_KEY);

  const openCollaboration = (collaborationId: string) => {
    markSeen(collaborationId);
    navigate({
      to: "/merchant/$merchantId/collaborations/target/$collaborationId",
      params: { merchantId: merchant, collaborationId },
    });
  };
  const totalGmvHidden = useFeatureFlag("F-total-gmv-hidden-collab-groups");
  const [expanded, setExpanded] = useState(false);
  const [automationsOpen, setAutomationsOpen] = useState(false);
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useSmartCollabGroupCollaborations(
    group.id,
    expanded,
  );
  const items = useMemo(() => data?.pages.flatMap((page) => page.collaborations) ?? [], [data]);
  const metricRows = useMemo(
    () => items.map((item) => ({ key: item.collaborationId, collaborationIds: [item.collaborationId] })),
    [items],
  );
  const { data: metrics } = useTargetCollaborationsMetrics(metricRows);

  const pinCollaboration = usePinSmartCollabGroupCollaboration(group.id);
  // A single mutation instance is shared by every row's pin button - derive which one is in
  // flight from `variables` so only that row shows a spinner, not all of them.
  const pendingCollaborationId = pinCollaboration.isPending ? pinCollaboration.variables?.collaborationId : undefined;

  return (
    <div className="flex flex-col">
      <ViewAutomationTriggersModal
        open={automationsOpen}
        onOpenChange={setAutomationsOpen}
        scope={{ kind: "group", groupId: group.id }}
        editDisabledReason={SMART_GROUP_EDIT_DISABLED_REASON}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setExpanded((v) => !v);
        }}
        className={cx(
          "relative flex w-full cursor-pointer items-center gap-4 border border-secondary bg-primary pl-6 pr-5 py-4 text-left",
          expanded ? "rounded-t-lg border-b-0" : "rounded-lg",
        )}
      >
        {group.isPinned && <div className="absolute inset-y-0 left-0 w-1 rounded-tl-md bg-brand-600" />}
        {expanded ? (
          <ChevronUp className="size-5 shrink-0 text-tertiary" />
        ) : (
          <ChevronDown className="size-5 shrink-0 text-tertiary" />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  markSeen(seenGroupId(group.id));
                  navigate({
                    to: "/merchant/$merchantId/collaborations/target-group/$groupId",
                    params: { merchantId: merchant, groupId: group.id },
                    search: { tab: "all", source: "smart-collab-group" },
                  });
                }}
                className={cx(
                  "text-[15px] font-medium text-primary hover:underline",
                  isSeen(seenGroupId(group.id)) && SEEN_ROW_CLASS,
                )}
              >
                {group.name}
              </button>
              <Tooltip
                title="Smart Collab Group"
                description="A group of collaborations created together, sharing the same products, commission rates, and sample settings. Click the row to expand and see individual collaborations."
                placement="top"
              >
                <TooltipTrigger className="inline-flex items-center">
                  <InfoCircle className="size-4 text-quaternary hover:text-tertiary cursor-help transition-colors" />
                </TooltipTrigger>
              </Tooltip>
            </div>
            <span className="inline-flex h-[21px] shrink-0 items-center gap-1 rounded-md border border-[#e9d7fe] bg-[#f9f5ff] pl-1.5 pr-2 py-0.5">
              <IntersectCircle className="size-3 text-[#6941c6]" />
              <span className="text-xs font-medium text-[#6941c6]">{group.collaborationCount} Collabs</span>
            </span>
            <RulesMatchIndicator variant="pill" rules={group.matchingRules} />
            <AutomationsActiveSignal
              variant="pill"
              automations={group.automations}
              onOpen={() => setAutomationsOpen(true)}
            />
          </div>
          <ProductChips products={group.topProducts} visible={2} groupTooltip />
        </div>
      </div>

      <div
        className={cx(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="overflow-x-auto rounded-b-md border-l border-r border-b border-primary">
            <div className="min-w-max">
              <div className="flex items-center border-b border-secondary bg-secondary">
                <div className="w-80 shrink-0 pl-16 py-2.5 text-xs font-semibold text-quaternary">Name</div>
                {totalGmvHidden && (
                  <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary">Total GMV</div>
                )}
                <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary">Modified</div>
                <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary">Expiry Status</div>
                <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary">Invite pending</div>
                <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary">Sample requests</div>
                <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary">Post Overdue</div>
                <div className="flex-1 px-5 py-2.5 text-xs font-semibold text-quaternary text-center">Videos</div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-tertiary">
                  <LoadingIndicator size="sm" />
                  <span className="text-xs">Loading collaborations…</span>
                </div>
              ) : (
                <>
                  {items.map((item) => {
                    const metric = metrics?.[item.collaborationId];
                    const invitePendingCount = resolveMetric(item.invitePendingCount, metric, "invitePendingCount");
                    const sampleRequestedCount = resolveMetric(
                      item.sampleRequestedCount,
                      metric,
                      "sampleRequestedCount",
                    );
                    const postOverdueCount = resolveMetric(item.postOverdueCount, metric, "postOverdueCount");
                    const postedVideosCount = resolveMetric(item.postedVideosCount, metric, "postedVideosCount");

                    const isRowPinned = item.isPinned ?? false;

                    return (
                      <div
                        key={item.collaborationId}
                        role="button"
                        tabIndex={0}
                        onClick={() => openCollaboration(item.collaborationId)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") openCollaboration(item.collaborationId);
                        }}
                        className="group/row relative flex min-h-[72px] cursor-pointer items-center border-b border-secondary bg-primary text-left transition-colors last:border-b-0 hover:bg-secondary"
                      >
                        <div
                          className={cx(
                            "flex w-80 shrink-0 flex-col justify-center gap-1 self-stretch border-l-4 py-3 pl-16 pr-5",
                            isRowPinned ? "border-brand-600" : "border-transparent",
                            isSeen(item.collaborationId) && SEEN_ROW_CLASS,
                          )}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              pinCollaboration.mutate({
                                collaborationId: item.collaborationId,
                                isPinned: !isRowPinned,
                              });
                            }}
                            className={cx(
                              "absolute left-5 top-1/2 -translate-y-1/2 transition-opacity duration-150 group-hover/row:opacity-100",
                              isRowPinned ? "opacity-100" : "opacity-0",
                            )}
                          >
                            {pendingCollaborationId === item.collaborationId ? (
                              <LoadingIndicator size="xs" />
                            ) : (
                              <Pin02
                                className={cx("size-[18px]", isRowPinned ? "text-brand-600" : "text-quaternary")}
                              />
                            )}
                          </button>
                          <span className="truncate text-sm font-medium text-primary">{item.name}</span>
                          <ProductChips products={item.topProducts} />
                        </div>
                        {totalGmvHidden && (
                          <div className="flex-1 px-5">
                            <GmvCell value={item.totalGmv} currency={item.totalGmvCurrency} />
                          </div>
                        )}
                        <div className="flex-1 px-5">
                          <ModifiedCell updatedAt={item.updatedAt} />
                        </div>
                        <div className="flex-1 px-5">
                          <ExpiryStatusCell status={item.expiryStatus} endTime={item.endTime} />
                        </div>
                        <div className="flex-1 px-5 text-center">
                          {invitePendingCount === null ? (
                            <MetricSkeleton />
                          ) : (
                            numberFormatter.format(invitePendingCount)
                          )}
                        </div>
                        <div className="flex-1 px-5 text-center">
                          {sampleRequestedCount === null ? (
                            <MetricSkeleton />
                          ) : (
                            numberFormatter.format(sampleRequestedCount)
                          )}
                        </div>
                        <div className="flex-1 px-5">
                          <OverdueCell count={postOverdueCount} />
                        </div>
                        <div className="flex-1 px-5 text-center">
                          {postedVideosCount === null ? <MetricSkeleton /> : numberFormatter.format(postedVideosCount)}
                        </div>
                      </div>
                    );
                  })}
                  {hasNextPage && <GroupExpansionSentinel sentinel={{ isFetchingNextPage, fetchNextPage }} />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
